import { useEffect, useRef, useState } from 'react';
import PaymentManager from '../../services/api/PaymentManager';
import { ERROR_CODES, PaymentResultErrorCode, PaymentStatus } from '../../services/exports/Constants';
import { PaymentResult } from '../../services/exports/Interfaces';
import paymentLog from '../../services/helpers/PaymentLogger';
import type { SpreedlyQrSheetState } from './useSpreedlyOffsitePayment';

export const QR_POLL_INTERVAL_MS = 4000;

// The bank refusing is the customer's answer, logged as such (PaymentResultErrorCode.Declined).
const failed: PaymentResult = {
  status: PaymentStatus.Failed,
  error: { code: PaymentResultErrorCode.Declined },
  result: null,
};
const paid: PaymentResult = { status: PaymentStatus.Succeeded, error: null, result: null };

/**
 * Milliseconds left on the code; Infinity when there is no expiry or it cannot be read, which would
 * otherwise poll in a tight loop and count down "NaN:NaN".
 */
export function msUntilQrExpiry(expiresAt: string | null): number {
  const at = expiresAt === null ? NaN : new Date(expiresAt).getTime();

  return Number.isFinite(at) ? at - Date.now() : Infinity;
}

/**
 * Asks the confirm endpoint what became of a Pix payment while its code is on screen. The acquirer's
 * callback settles it server-side; this only learns the answer: 200 paid, 409 refused, 402 still
 * waiting (ask again). Asks one last time the moment the code expires, since a customer who paid
 * in its final seconds is paid, then stops — the backend gives the payment up itself.
 */
export default function useSpreedlyQrPolling(sheet: SpreedlyQrSheetState, onSettled: (result: PaymentResult) => void) {
  const [expired, setExpired] = useState(false);
  const [checking, setChecking] = useState(false);
  const settledRef = useRef(false);
  const stopRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    let asking = false;
    const logAttrs = { order_uuid: sheet.orderUuid, payment_method: 'pix' };

    const settle = (result: PaymentResult) => {
      if (settledRef.current || cancelled) {
        return;
      }

      settledRef.current = true;
      onSettled(result);
    };

    const msUntilExpiry = () => msUntilQrExpiry(sheet.expiresAt);

    // The last wait is cut to the expiry itself, so the final ask lands on it rather than up to a
    // whole interval early.
    const scheduleNext = () => {
      timer = setTimeout(poll, Math.max(0, Math.min(QR_POLL_INTERVAL_MS, msUntilExpiry())));
    };

    const poll = async () => {
      if (cancelled || asking) {
        return;
      }

      timer = null;
      asking = true;

      const isFinal = msUntilExpiry() <= 0;

      const { success, status } = await PaymentManager.submitSpreedlyPaymentDetails(
        { company_id: sheet.companyId, transaction_token: sheet.transactionToken, order_uuid: sheet.orderUuid },
        { [ERROR_CODES.CARD_EXCEPTION]: () => null },
      );

      asking = false;

      if (success) {
        paymentLog.info('spreedly', 'offsite_qr_paid', logAttrs);
        settle(paid);

        return;
      }

      if (status === 409) {
        paymentLog.info('spreedly', 'offsite_qr_refused', logAttrs);
        settle(failed);

        return;
      }

      if (status !== 402) {
        // Not an answer about the payment (a network error, a 5xx): keep asking rather than tell a
        // customer who may already have paid that it failed.
        paymentLog.warn('spreedly', 'offsite_qr_poll_failed', { ...logAttrs, status });
      }

      if (isFinal) {
        if (!cancelled) {
          paymentLog.info('spreedly', 'offsite_qr_expired', logAttrs);
          setExpired(true);
        }

        return;
      }

      scheduleNext();
    };

    // On a phone the customer pays in their bank app, and browsers slow a hidden tab's timers to
    // about one a minute. Coming back should not mean waiting for that: ask straight away.
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || cancelled || asking || timer === null) {
        return;
      }

      clearTimeout(timer);
      timer = null;
      poll();
    };

    const stop = () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);

      if (timer) {
        clearTimeout(timer);
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    stopRef.current = stop;
    scheduleNext();

    return stop;
  }, [sheet.transactionToken]);

  /**
   * The customer closes the sheet: asked once more first, since they may have paid in the seconds
   * since the last ask (or in the bank app while the sheet was open). Paid settles paid — the
   * order goes ahead, and telling them it failed would have them pay twice; anything else is their
   * cancel.
   */
  const closeAfterCheck = async (onNotPaid: () => void): Promise<void> => {
    if (settledRef.current || checking) {
      return;
    }

    stopRef.current();
    setChecking(true);

    const { success } = await PaymentManager.submitSpreedlyPaymentDetails(
      { company_id: sheet.companyId, transaction_token: sheet.transactionToken, order_uuid: sheet.orderUuid },
      { [ERROR_CODES.CARD_EXCEPTION]: () => null },
    );

    setChecking(false);

    if (success) {
      paymentLog.info('spreedly', 'offsite_qr_paid_on_close', { order_uuid: sheet.orderUuid, payment_method: 'pix' });
      settledRef.current = true;
      onSettled(paid);

      return;
    }

    onNotPaid();
  };

  return { expired, checking, closeAfterCheck };
}
